import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { useWorkspace } from '../../../context/useWorkspace'
import { AppDialog, Button, Select, StatusPill, TextInput } from '../../Common'
import type { SelectOption } from '../../Common'
import {
  getBmwStatus,
  saveBmwDay,
  submitBmwDay,
  correctBmwDay,
} from '../../../services/hosBmwService'
import { queueOpsBmwSave } from '../../../services/helper/queueService'
import { createOpId } from '../../../services/helper/opId'
import {
  getSharedDeviceWorkRouterContext,
  routeBmwSave,
} from '../../../services/sharedDeviceWorkRouter'
import { workspaceTodayKey } from '../../../services/repeat/keys'
import { getApiErrorMessage } from '../../../services/apiConnectivity'
import { BMW_CATEGORIES, type BmwCategory, type BmwStatusResult, type OpsPlace } from '../../../types/ops'
import styles from './BmwPhoneSection.module.css'

export interface BmwPhoneSectionProps {
  workspaceId: string
  callerUid: string
  callerRole?: string | null
  isOwner?: boolean
  isManager?: boolean
  sectorRoles?: string[]
  allowCorrection?: boolean
  places: OpsPlace[]
  isSharedDevice?: boolean
  effectiveDevicePlaceId?: string
  onError?: (err: string | null) => void
}

// In-memory status cache for offline resilience across facility switches and component mounts
const facilityStatusCache = new Map<string, BmwStatusResult>()

export function BmwPhoneSection({
  workspaceId,
  callerRole,
  allowCorrection = true,
  places,
  isSharedDevice,
  effectiveDevicePlaceId,
  onError,
}: BmwPhoneSectionProps): React.ReactElement {
  const { t } = useNls()

  const { workspaces } = useWorkspace()
  const workspace = workspaces.find((w) => w.id === workspaceId)
  const todayKey = useMemo(
    () => workspaceTodayKey('DAILY', workspace?.timezone),
    [workspace?.timezone],
  )

  // Candidate facilities: facility level only, restricted scope respected
  const candidateFacilities = useMemo(() => {
    const list = places.filter((p) => !p.archived)
    const hasFacilityLevel = list.some((p) => p.level === 'FACILITY')
    const pool = list.filter((p) => (hasFacilityLevel ? p.level === 'FACILITY' : !p.parentId))

    return pool.filter((p) => {
      if (isSharedDevice && effectiveDevicePlaceId) {
        return p.id === effectiveDevicePlaceId
      }
      return true
    })
  }, [places, isSharedDevice, effectiveDevicePlaceId])

  const [userSelectedPlaceId, setUserSelectedPlaceId] = useState<string>('')

  const selectedPlaceId = useMemo(() => {
    if (userSelectedPlaceId && candidateFacilities.some((f) => f.id === userSelectedPlaceId)) {
      return userSelectedPlaceId
    }
    if (isSharedDevice && effectiveDevicePlaceId) return effectiveDevicePlaceId
    return candidateFacilities[0]?.id || ''
  }, [userSelectedPlaceId, candidateFacilities, isSharedDevice, effectiveDevicePlaceId])

  const facilityOptions: SelectOption<string>[] = useMemo(() => {
    return candidateFacilities.map((f) => ({
      value: f.id || '',
      label: f.name || f.id || '',
    }))
  }, [candidateFacilities])

  // BMW status & form state
  const [bmwStatus, setBmwStatus] = useState<BmwStatusResult | null>(null)
  const [bmwWeights, setBmwWeights] = useState<Record<BmwCategory, string>>({
    YELLOW: '0',
    RED: '0',
    WHITE: '0',
    BLUE: '0',
  })
  const [bmwBagCounts, setBmwBagCounts] = useState<Record<BmwCategory, string>>({
    YELLOW: '',
    RED: '',
    WHITE: '',
    BLUE: '',
  })
  const [dayLocked, setDayLocked] = useState(false)
  const [busy, setBusy] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [statusMsg, setStatusMsg] = useState<string | null>(null)
  const [unusualModalOpen, setUnusualModalOpen] = useState(false)
  const [correctModalOpen, setCorrectModalOpen] = useState(false)
  const [correctionReason, setCorrectionReason] = useState('')
  const [refreshNonce, setRefreshNonce] = useState(0)

  // Live online/offline state
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  )

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline)
      window.addEventListener('offline', handleOffline)
      return () => {
        window.removeEventListener('online', handleOnline)
        window.removeEventListener('offline', handleOffline)
      }
    }
  }, [])

  const applyLoadedStatus = useCallback((res: BmwStatusResult) => {
    setBmwStatus(res)
    if (res.day) {
      if (res.day.state === 'LOCKED') {
        setDayLocked(true)
        setBmwWeights({
          YELLOW: String(res.day.weights?.YELLOW ?? 0),
          RED: String(res.day.weights?.RED ?? 0),
          WHITE: String(res.day.weights?.WHITE ?? 0),
          BLUE: String(res.day.weights?.BLUE ?? 0),
        })
        setBmwBagCounts({
          YELLOW: res.day.bagCounts?.YELLOW != null ? String(res.day.bagCounts.YELLOW) : '',
          RED: res.day.bagCounts?.RED != null ? String(res.day.bagCounts.RED) : '',
          WHITE: res.day.bagCounts?.WHITE != null ? String(res.day.bagCounts.WHITE) : '',
          BLUE: res.day.bagCounts?.BLUE != null ? String(res.day.bagCounts.BLUE) : '',
        })
      } else if (res.day.state === 'DRAFT') {
        setDayLocked(false)
        setBmwWeights({
          YELLOW: String(res.day.weights?.YELLOW ?? 0),
          RED: String(res.day.weights?.RED ?? 0),
          WHITE: String(res.day.weights?.WHITE ?? 0),
          BLUE: String(res.day.weights?.BLUE ?? 0),
        })
        setBmwBagCounts({
          YELLOW: res.day.bagCounts?.YELLOW != null ? String(res.day.bagCounts.YELLOW) : '',
          RED: res.day.bagCounts?.RED != null ? String(res.day.bagCounts.RED) : '',
          WHITE: res.day.bagCounts?.WHITE != null ? String(res.day.bagCounts.WHITE) : '',
          BLUE: res.day.bagCounts?.BLUE != null ? String(res.day.bagCounts.BLUE) : '',
        })
      } else {
        setDayLocked(false)
      }
    }
  }, [])

  const fetchStatus = useCallback(async () => {
    if (!workspaceId || !selectedPlaceId) return
    try {
      const res = await getBmwStatus({ workspaceId, placeId: selectedPlaceId, dayKey: todayKey })
      facilityStatusCache.set(selectedPlaceId, res)
      applyLoadedStatus(res)
    } catch (err: unknown) {
      const cached = facilityStatusCache.get(selectedPlaceId)
      if (cached) {
        applyLoadedStatus(cached)
      }
      const msg = getApiErrorMessage(err)
      setErrorMsg(msg)
      onError?.(msg)
    }
  }, [workspaceId, selectedPlaceId, todayKey, applyLoadedStatus, onError])

  useEffect(() => {
    let ignore = false
    if (!workspaceId || !selectedPlaceId) return

    void getBmwStatus({ workspaceId, placeId: selectedPlaceId, dayKey: todayKey })
      .then((res) => {
        if (ignore) return
        facilityStatusCache.set(selectedPlaceId, res)
        applyLoadedStatus(res)
      })
      .catch((err: unknown) => {
        if (ignore) return
        const cached = facilityStatusCache.get(selectedPlaceId)
        if (cached) {
          applyLoadedStatus(cached)
        }
        const msg = getApiErrorMessage(err)
        setErrorMsg(msg)
        onError?.(msg)
      })

    return () => {
      ignore = true
    }
  }, [workspaceId, selectedPlaceId, todayKey, applyLoadedStatus, onError, refreshNonce])

  const parseWeights = (): Record<BmwCategory, number> => ({
    YELLOW: Math.max(0, parseFloat(bmwWeights.YELLOW) || 0),
    RED: Math.max(0, parseFloat(bmwWeights.RED) || 0),
    WHITE: Math.max(0, parseFloat(bmwWeights.WHITE) || 0),
    BLUE: Math.max(0, parseFloat(bmwWeights.BLUE) || 0),
  })

  const parseBagCounts = (): Partial<Record<BmwCategory, number>> | undefined => {
    const res: Partial<Record<BmwCategory, number>> = {}
    let hasCount = false
    for (const cat of BMW_CATEGORIES) {
      const raw = bmwBagCounts[cat]
      if (raw !== undefined && raw.trim() !== '') {
        const count = parseInt(raw, 10)
        if (!isNaN(count) && count >= 0) {
          res[cat] = count
          hasCount = true
        }
      }
    }
    return hasCount ? res : undefined
  }

  const handleSaveDraft = async () => {
    if (!selectedPlaceId) return
    setBusy(true)
    setErrorMsg(null)
    setStatusMsg(null)
    const weights = parseWeights()
    const bagCounts = parseBagCounts()
    const opId = createOpId()

    try {
      const routerCtx = getSharedDeviceWorkRouterContext()
      if (routerCtx) {
        await routeBmwSave(
          {
            workspaceId,
            placeId: selectedPlaceId,
            dayKey: todayKey,
            weights,
            bagCounts,
            opId,
            clientMutationId: opId,
          },
          routerCtx,
        )
        setStatusMsg(t('NLSOpsHosBmwDailyDraftSaved'))
      } else if (!isOnline) {
        await queueOpsBmwSave({
          workspaceId,
          placeId: selectedPlaceId,
          dayKey: todayKey,
          weights,
          bagCounts,
          opId,
          clientMutationId: opId,
        })
        setStatusMsg(t('NLSOpsHosBmwDailyDraftSaved'))
      } else {
        await saveBmwDay({
          workspaceId,
          placeId: selectedPlaceId,
          dayKey: todayKey,
          weights,
          bagCounts,
          opId,
        })
        setStatusMsg(t('NLSOpsHosBmwDailyDraftSaved'))
        setRefreshNonce((n) => n + 1)
      }
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err)
      setErrorMsg(msg)
      onError?.(msg)
    } finally {
      setBusy(false)
    }
  }

  const handleSubmit = async (confirmUnusual = false) => {
    if (!selectedPlaceId || !isOnline) return
    setBusy(true)
    setErrorMsg(null)
    setStatusMsg(null)
    const weights = parseWeights()
    const bagCounts = parseBagCounts()
    const opId = createOpId()

    try {
      await submitBmwDay({
        workspaceId,
        placeId: selectedPlaceId,
        dayKey: todayKey,
        weights,
        bagCounts,
        confirmUnusual,
        opId,
      })
      setDayLocked(true)
      setUnusualModalOpen(false)
      setStatusMsg(t('NLSOpsHosBmwDailyLocked'))
      setRefreshNonce((n) => n + 1)
      await fetchStatus()
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err)
      if (msg.includes('UNUSUAL_WEIGHT_CONFIRMATION_REQUIRED') || msg.includes('Unusual weight')) {
        setUnusualModalOpen(true)
      } else {
        setErrorMsg(msg)
        onError?.(msg)
      }
    } finally {
      setBusy(false)
    }
  }

  const handleCorrect = async () => {
    if (!selectedPlaceId || correctionReason.trim().length < 3) return
    setBusy(true)
    setErrorMsg(null)
    setStatusMsg(null)
    const weights = parseWeights()
    const bagCounts = parseBagCounts()
    const opId = createOpId()

    try {
      await correctBmwDay({
        workspaceId,
        placeId: selectedPlaceId,
        dayKey: todayKey,
        weights,
        bagCounts,
        reason: correctionReason.trim(),
        opId,
      })
      setCorrectModalOpen(false)
      setCorrectionReason('')
      setStatusMsg(t('NLSOpsHosBmwCorrectionSaved'))
      setRefreshNonce((n) => n + 1)
      await fetchStatus()
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err)
      setErrorMsg(msg)
      onError?.(msg)
    } finally {
      setBusy(false)
    }
  }

  const categoryLabels: Record<BmwCategory, string> = {
    YELLOW: t('NLSOpsHosBmwYellow'),
    RED: t('NLSOpsHosBmwRed'),
    WHITE: t('NLSOpsHosBmwWhite'),
    BLUE: t('NLSOpsHosBmwBlue'),
  }

  const activeStatus = bmwStatus ?? facilityStatusCache.get(selectedPlaceId)

  // Server is source of truth for write/correct access.
  // When status is unknown (offline, uncached), save draft stays available only to roles the server could allow.
  const canWrite = activeStatus != null
    ? Boolean(activeStatus.canWrite)
    : (callerRole !== 'VIEWER')

  const canCorrect = activeStatus != null
    ? Boolean(activeStatus.canCorrect)
    : false

  if (candidateFacilities.length === 0) {
    return (
      <section className={styles.section} data-testid="ops-section-bmw" aria-labelledby="bmw-title">
        <h3 id="bmw-title" className={styles.sectionTitle}>
          {t('NLSOpsHosBmwDailyTitle')}
        </h3>
        <p className={styles.offlineHint}>{t('NLSHelperOpsBmwNoFacilities')}</p>
      </section>
    )
  }

  return (
    <section className={styles.section} data-testid="ops-section-bmw" aria-labelledby="bmw-title">
      <div className={styles.sectionHeader}>
        <div className={styles.titleGroup}>
          <h3 id="bmw-title" className={styles.sectionTitle}>
            {t('NLSOpsHosBmwDailyTitle')}
          </h3>
          <p className={styles.sectionDesc}>
            {todayKey}
          </p>
        </div>
        {dayLocked && (
          <span data-testid="ops-bmw-locked-pill">
            <StatusPill tone="neutral">
              {t('NLSHelperOpsBmwSubmitted')}
            </StatusPill>
          </span>
        )}
      </div>

      {errorMsg && (
        <div className={styles.errorBanner} role="alert" data-testid="ops-bmw-error-banner">
          {errorMsg}
        </div>
      )}

      {statusMsg && (
        <div className={styles.successBanner} data-testid="ops-bmw-status-banner">
          {statusMsg}
        </div>
      )}

      {/* Facility Picker */}
      <div className={styles.inputGroup} data-testid="ops-bmw-facility-select">
        <label htmlFor="ops-bmw-facility" className={styles.fieldLabel}>
          {t('NLSHelperOpsBmwFacility')}
        </label>
        <Select
          id="ops-bmw-facility"
          ariaLabel={t('NLSHelperOpsBmwFacility')}
          value={selectedPlaceId}
          options={facilityOptions}
          onChange={(val) => setUserSelectedPlaceId(val)}
          disabled={busy || candidateFacilities.length <= 1}
        />
      </div>

      {/* Storage Timer Status */}
      {bmwStatus && (
        <div className={styles.statusRow} data-testid="ops-bmw-status-row">
          <span className={styles.statusLabel}>{t('NLSOpsHosBmwStorageTimer')}:</span>
          <span className={styles.statusValue}>
            {bmwStatus.hoursStored}h / {bmwStatus.storageHoursLimit}h
          </span>
          {bmwStatus.status === 'EXCEEDED' && (
            <StatusPill tone="danger">
              {t('NLSOpsHosBmwStorageExceeded')}
            </StatusPill>
          )}
          {bmwStatus.status === 'OK' && (
            <StatusPill tone="success">
              {t('NLSOpsHosBmwStorageOk')}
            </StatusPill>
          )}
        </div>
      )}

      {/* Category Inputs */}
      {canWrite && (
        <div className={styles.categoryGrid}>
          {BMW_CATEGORIES.map((cat) => (
            <div key={cat} className={styles.categoryCard} data-testid={`ops-bmw-card-${cat}`}>
              <div className={styles.categoryHeader}>
                <span>{categoryLabels[cat]}</span>
              </div>
              <div className={styles.inputsRow}>
                <div className={styles.inputGroup}>
                  <label htmlFor={`ops-bmw-weight-${cat}`} className={styles.fieldLabel}>
                    {t('NLSHelperOpsBmwKg')}
                  </label>
                  <TextInput
                    id={`ops-bmw-weight-${cat}`}
                    data-testid={`ops-bmw-weight-${cat}`}
                    type="number"
                    value={bmwWeights[cat]}
                    readOnly={dayLocked}
                    disabled={busy}
                    onChange={(val) => setBmwWeights((prev) => ({ ...prev, [cat]: val }))}
                    placeholder="0.0"
                  />
                </div>
                <div className={styles.inputGroup}>
                  <label htmlFor={`ops-bmw-bags-${cat}`} className={styles.fieldLabel}>
                    {t('NLSHelperOpsBmwBags')}
                  </label>
                  <TextInput
                    id={`ops-bmw-bags-${cat}`}
                    data-testid={`ops-bmw-bags-${cat}`}
                    type="number"
                    value={bmwBagCounts[cat]}
                    readOnly={dayLocked}
                    disabled={busy}
                    onChange={(val) => setBmwBagCounts((prev) => ({ ...prev, [cat]: val }))}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      <div className={styles.actionRow}>
        {canWrite && !dayLocked && (
          <>
            <Button
              variant="secondary"
              data-testid="ops-bmw-save-draft-btn"
              disabled={busy}
              onClick={handleSaveDraft}
            >
              {t('NLSHelperOpsBmwSaveDraft')}
            </Button>

            <Button
              variant="primary"
              data-testid="ops-bmw-submit-btn"
              disabled={busy || !isOnline}
              onClick={() => handleSubmit(false)}
            >
              {t('NLSOpsHosBmwSubmitDay')}
            </Button>
          </>
        )}

        {allowCorrection && canCorrect && (
          <Button
            variant="secondary"
            data-testid="ops-bmw-correct-btn"
            disabled={busy}
            onClick={() => setCorrectModalOpen(true)}
          >
            {t('NLSOpsHosBmwCorrectDay')}
          </Button>
        )}

        {canWrite && !dayLocked && !isOnline && (
          <p className={styles.offlineHint} data-testid="ops-bmw-offline-hint">
            {t('NLSHelperOpsBmwNeedsConnection')}
          </p>
        )}
      </div>

      {/* Unusual Weight Confirmation Dialog */}
      {unusualModalOpen && (
        <AppDialog
          open={unusualModalOpen}
          onDismiss={() => setUnusualModalOpen(false)}
          title={t('NLSOpsHosBmwConfirmUnusual')}
          actions={[
            {
              label: t('NLSOpsPlacesCancel'),
              variant: 'ghost',
              onClick: () => setUnusualModalOpen(false),
            },
            {
              label: t('NLSOpsHosBmwConfirmAndSubmit'),
              variant: 'primary',
              onClick: () => handleSubmit(true),
            },
          ]}
        >
          <div className={styles.dialogBody} data-testid="ops-bmw-unusual-dialog">
            <p>{t('NLSOpsHosBmwConfirmUnusualDesc')}</p>
            <div className={styles.actionRow}>
              <Button
                variant="primary"
                data-testid="ops-bmw-confirm-unusual-btn"
                onClick={() => handleSubmit(true)}
              >
                {t('NLSOpsHosBmwConfirmAndSubmit')}
              </Button>
            </div>
          </div>
        </AppDialog>
      )}

      {/* Member Correction Dialog */}
      {correctModalOpen && (
        <AppDialog
          open={correctModalOpen}
          onDismiss={() => setCorrectModalOpen(false)}
          title={t('NLSOpsHosBmwCorrectDay')}
          actions={[
            {
              label: t('NLSOpsPlacesCancel'),
              variant: 'ghost',
              onClick: () => setCorrectModalOpen(false),
            },
            {
              label: t('NLSMemberOpsBmwCorrect'),
              variant: 'primary',
              disabled: correctionReason.trim().length < 3 || busy,
              onClick: handleCorrect,
            },
          ]}
        >
          <div className={styles.dialogBody} data-testid="ops-bmw-correct-dialog">
            <label htmlFor="ops-bmw-correction-reason" className={styles.fieldLabel}>
              {t('NLSMemberOpsBmwReason')}
            </label>
            <TextInput
              id="ops-bmw-correction-reason"
              data-testid="ops-bmw-correct-reason"
              value={correctionReason}
              onChange={(val) => setCorrectionReason(val)}
              placeholder="e.g. Scale sensor recalibrated"
            />
            <div className={styles.actionRow}>
              <Button
                variant="primary"
                data-testid="ops-bmw-confirm-correct-btn"
                disabled={correctionReason.trim().length < 3 || busy}
                onClick={handleCorrect}
              >
                {t('NLSMemberOpsBmwCorrect')}
              </Button>
            </div>
          </div>
        </AppDialog>
      )}
    </section>
  )
}
